<template>
  <tab-layout :full-width="true">
    <div
      slot="tabContent"
      class="add-nomination-container"
      :style="appearances.schema_color 
        ? { 
          '--primary': appearances.schema_color,  
          '--primary-dark': appearances.schema_color,
          '--primary-rgb': appearances.schema_color,
        } : null"
      >
      <div class="leaderboard nominate bg-white rounded mb-4">

        <!-- Header: title + top sponsor logos (always rendered so AppAlert is mounted) -->
        <div class="p-3">
          <page-heading>Nominate for {{ draft ? draft.name : '' }}</page-heading>
          <appearances-drafts-header :appearances="appearances" />
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>

        <template v-else-if="!loadFailed">


          <!-- Biosecurity block -->
              <div v-if="draft && draft.settings && draft.settings.is_show_biosecurity_form" class="mt-2 mb-3 px-3">
                <div class="d-flex h5">Biosecurity Form</div>
                <div class="d-flex flex-wrap biosecurity-actions">
                  <router-link :to="`/biosecurity/${draft.id}`" class="btn btn-primary btn-sm">
                    Fill Online
                  </router-link>
                  <app-button type="button" variant="outline-primary" size="sm" :block="false" class="d-flex align-items-center biosecurity-actions__download" :loading="downloadingBlankForm" @click="downloadBlankForm"><svg class="mr-2" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 10.133c-.107 0-.32 0-.427-.107L4.373 6.827a.52.52 0 010-.747.52.52 0 01.747 0L7.893 8.853l2.774-2.773a.52.52 0 01.746 0 .52.52 0 010 .747L8.213 10.026c.107.107-.107.107-.213.107z" fill="currentColor"/><path d="M8 9.6c-.32 0-.533-.213-.533-.533V.533C7.467.213 7.68 0 8 0c.32 0 .533.213.533.533v8.534c0 .32-.213.533-.533.533z" fill="currentColor"/><path d="M13.333 16H2.667C1.173 16 0 14.827 0 13.333V11.2c0-.32.213-.533.533-.533.32 0 .534.213.534.533v2.133c0 .854.746 1.6 1.6 1.6h10.666c.854 0 1.6-.746 1.6-1.6V11.2c0-.32.214-.533.534-.533.32 0 .533.213.533.533v2.133C16 14.827 14.827 16 13.333 16z" fill="currentColor"/></svg>Download Blank Form</app-button>
                </div>
              </div>
          <!-- Tab switcher -->
          <div class="filter mb-0 px-3">
            <app-tab-switcher
              :items="tabItems"
              :value="activeTab"
              :color="appearances.schema_color || null"
              @input="activeTab = $event"
            />
          </div>

          <!-- Main layout: form + desktop banner -->
          <div class="nom-add__layout nom-add__layout--with-banner mt-3">
            <div class="nominations p-3 bg-lighter border-top nom-add__forms">

              <!-- Secretary notice -->
              <div v-if="$isSecretaryOrAdmin" class="alert alert-info alert-simple">
                As a secretary you're able to nominate for all members in the ACA database.
                If you can't find the member you're nominating for, click
                <router-link :to="{ name: 'Members' }">HERE</router-link>
                to check if they're financial.
              </div>

              <!-- No-horse tab hint (Nominate by event only) -->
              <div v-if="activeTab === 'event' && hasNoHorseEvents" class="alert alert-info alert-simple" role="alert">
                If you can’t find what you’re looking for here – and for events like judges seminars, clinics, meals, special awards and stables – try looking under the
                <a href="#" @click.prevent="activeTab = 'submission'">“No horse required”</a> tab.
              </div>

              <!-- Draft info (shared) -->
              <div v-if="draft" class="wrap-nominations-item">
                <div class="nominations__item">
                  <span class="font-weight-bold">Draft Start Date:</span>
                  <span class="nominations__item-date ml-2">{{ formatDate(draft.start_date) }}</span>
                </div>
                <div class="nominations__item">
                  <span class="font-weight-bold">Noms Open till:</span>
                  <span class="nominations__item-date ml-2">{{ formatDate(draft.noms_closed) }}</span>
                </div>
                <div v-if="draft.settings && draft.settings.event_members_allowed" class="nominations__item">
                  Event members: allowed
                </div>
                <div class="nominations__item">
                  <span class="font-weight-bold">First Round Runs Per Person:</span>
                  <span class="nominations__item-date ml-2">{{ draft.settings && draft.settings.first_round_runs_per_person != null ? draft.settings.first_round_runs_per_person : 'Unlimited' }}</span>
                </div>
                <div class="nominations__item">
                  <span class="font-weight-bold">Total Runs Per Horse:</span>
                  <span class="nominations__item-date ml-2">{{ draft.settings && draft.settings.total_runs_per_horse != null ? draft.settings.total_runs_per_horse : 'Unlimited' }}</span>
                </div>
              </div>
              <!-- needs_to_be_processed warning -->
              <div v-if="needsToBeProcessed && draft" class="alert alert-warning needs_to_be_processed_message mt-2">
                Due to excessive nominations {{ draft.name }} restrictions have been changed.<br>
                <template v-if="draft.settings.previous_first_round_runs_per_person != null && draft.settings.previous_first_round_runs_per_person !== draft.settings.first_round_runs_per_person">
                  First round runs per person changed from {{ draft.settings.previous_first_round_runs_per_person }} to {{ draft.settings.first_round_runs_per_person }}.<br>
                </template>
                <template v-if="draft.settings.previous_total_runs_per_horse != null && draft.settings.previous_total_runs_per_horse !== draft.settings.total_runs_per_horse">
                  Total runs per horse changed from {{ draft.settings.previous_total_runs_per_horse }} to {{ draft.settings.total_runs_per_horse }}.<br>
                </template>
              </div>

              <!-- Member select (shared) -->
              <div class="form-group wrap-custom-select _type-add-horse-select nom-add__field d-flex align-items-start">
                <label class="font-weight-bold mr-3 nom-add__label">Choose the competitor<br>to nominate for</label>
                <div class="flex-grow-1 nom-add__select">
                  <multiselect
                    v-model="selectedMember"
                    :options="memberSearchResults"
                    :loading="loadingMembers"
                    label="full_name"
                    track-by="id"
                    placeholder="Search member..."
                    select-label=""
                    deselect-label=""
                    selected-label=""
                    @open="onMemberOpen"
                    @search-change="searchMembers"
                    @input="onMemberInput"
                  >
                    <template #singleLabel="{ option }">
                      {{ option.full_name }}<span v-if="$isSecretaryOrAdmin && memberVolunteerNote(option)" class="text-muted"> ({{ memberVolunteerNote(option) }})</span>
                    </template>
                    <template #option="{ option }">
                      {{ option.full_name }}<span v-if="$isSecretaryOrAdmin && memberVolunteerNote(option)" class="text-muted"> ({{ memberVolunteerNote(option) }})</span>
                    </template>
                    <template #noResult>No results found.</template>
                  </multiselect>
                </div>
              </div>

              <!-- ========== Nominate by Event ========== -->
              <template v-if="activeTab === 'event'">

                <!-- Event select -->
                <div class="form-group wrap-custom-select _type-add-horse-select nom-add__field d-flex">
                  <label class="font-weight-bold mr-3 nom-add__label">Choose the event</label>
                  <div class="flex-grow-1 nom-add__select">
                    <multiselect
                      v-model="byEvent.selectedEvent"
                      :options="draftEvents"
                      label="name"
                      track-by="id"
                      placeholder="Select event..."
                      select-label=""
                      deselect-label=""
                      selected-label=""
                      @input="onByEventEventInput"
                    >
                      <template #singleLabel="{ option }">{{ option.name }} — ${{ option.cost }}</template>
                      <template #option="{ option }">{{ option.name }} — ${{ option.cost }}</template>
                      <template #noResult>No events found.</template>
                    </multiselect>
                  </div>
                </div>

                <!-- Max runs per event -->
                <div class="nominations__item">
                  <span class="nominations__item-title font-weight-bold">Max runs per event:</span>
                  <span class="nominations__item-date ml-2">
                    <template v-if="byEvent.eventId">
                      {{ byEvent.maxHorses != null ? byEvent.maxHorses : '—' }}
                    </template>
                    <template v-else>Choose the event</template>
                  </span>
                </div>

                <!-- Horses -->
                <div class="form-group wrap-added-item mt-2">
                  <label class="font-weight-bold">Choose the horses</label>
                  <div v-if="byEvent.loadingHorses" class="text-muted">
                    <span class="spinner-border spinner-border-sm mr-1" />
                    Loading horses...
                  </div>
                  <div v-else-if="byEvent.eventId && byEvent.horses.length === 0 && selectedMember" class="text-muted">
                    No eligible horses found for this event.
                  </div>
                  <div v-else class="available-horses">
                    <app-tooltip
                      v-for="horse in byEvent.horses"
                      :key="horse.id"
                      :text="horse.disabled_reason || ''"
                    >
                      <f-checkbox
                        :id="`by-event-horse-${horse.id}`"
                        :label="horse.name"
                        :value="byEvent.selectedHorseIds.indexOf(horse.id) !== -1"
                        :font-normal="true"
                        :disabled="!!horse.disabled"
                        @input="onHorseToggle(horse.id)"
                      />
                    </app-tooltip>
                  </div>

                  <!-- Selected horses order preview -->
                  <div v-if="orderedSelectedHorses.length > 0" class="horse-order-preview mt-3">
                    <draggable
                      :list="orderedSelectedHorses"
                      @end="onPreviewDragEnd"
                    >
                      <div
                        v-for="(horse, index) in orderedSelectedHorses"
                        :key="horse.id"
                        class="horse-order-preview__item"
                      >
                        <span class="horse-order-preview__num">{{ index + 1 }}</span> {{ horse.name }}
                      </div>
                    </draggable>
                  </div>
                </div>

              </template>

              <!-- ========== Nominate by Horse ========== -->
              <template v-if="activeTab === 'horse'">

                <!-- Horse select -->
                <div class="form-group wrap-custom-select _type-add-horse-select nom-add__field d-flex align-items-center">
                  <label class="font-weight-bold mr-3 nom-add__label">Choose the horse</label>
                  <div class="flex-grow-1 nom-add__select">
                    <multiselect
                      v-model="byHorse.selectedHorse"
                      :options="byHorse.horses"
                      :loading="byHorse.loadingHorses"
                      :disabled="!selectedMember"
                      label="name"
                      track-by="id"
                      placeholder="Select horse..."
                      select-label=""
                      deselect-label=""
                      selected-label=""
                      @input="onByHorseHorseInput"
                    >
                      <template #noResult>No horses found.</template>
                    </multiselect>
                  </div>
                </div>

                <!-- Events for horse -->
                <div class="form-group wrap-added-item mt-2">
                  <label class="font-weight-bold">Choose the event</label>
                  <div v-if="byHorse.loadingEvents" class="text-muted">
                    <span class="spinner-border spinner-border-sm mr-1" />
                    Loading events...
                  </div>
                  <div v-else-if="byHorse.horseId && byHorse.events.length === 0" class="text-muted">
                    No eligible events found for this horse.
                  </div>
                  <app-tooltip
                    v-for="ev in byHorse.events"
                    :key="ev.id"
                    :text="eventNomTooltip(ev)"
                    class="mb-2"
                  >
                    <div class="custom-control custom-radio">
                      <input
                        :id="`by-horse-event-${ev.id}`"
                        v-model="byHorse.selectedEventId"
                        type="radio"
                        class="custom-control-input"
                        :value="ev.id"
                        :disabled="!!ev.nominated || !!ev.nominated_in_combined"
                        name="byHorseEvent"
                      >
                      <label :for="`by-horse-event-${ev.id}`" class="custom-control-label">
                        {{ ev.name }} — ${{ ev.cost }}
                      </label>
                    </div>
                  </app-tooltip>
                </div>

              </template>

              <!-- ========== Nominate without a horse (award/stable/catering) ========== -->
              <template v-if="activeTab === 'submission'">

                <!-- Event select -->
                <div class="form-group wrap-custom-select _type-add-horse-select nom-add__field d-flex">
                  <label class="font-weight-bold mr-3 nom-add__label">Choose the event</label>
                  <div class="flex-grow-1 nom-add__select">
                    <multiselect
                      v-model="bySubmission.selectedEvent"
                      :options="bySubmission.events"
                      label="name"
                      track-by="id"
                      placeholder="Select event..."
                      select-label=""
                      deselect-label=""
                      selected-label=""
                      @input="onBySubmissionEventInput"
                    >
                      <template #singleLabel="{ option }">{{ option.name }} — ${{ option.cost }}</template>
                      <template #option="{ option }">{{ option.name }} — ${{ option.cost }}</template>
                      <template #noResult>No events found.</template>
                    </multiselect>
                  </div>
                </div>

                <!-- Max submissions -->
                <div class="nominations__item">
                  <span class="nominations__item-title font-weight-bold">Maximum Submissions Per Person:</span>
                  <span class="nominations__item-date ml-2">
                    <template v-if="bySubmission.eventId">
                      <template v-if="bySubmission.maxSubmissions != null">
                        {{ bySubmission.used }} / {{ bySubmission.maxSubmissions }} used
                      </template>
                      <template v-else>Unlimited</template>
                    </template>
                    <template v-else>Choose the event</template>
                  </span>
                </div>

                <!-- Quantity -->
                <div class="form-group wrap-added-item mt-2" style="max-width: 200px;">
                  <f-text
                    id="submission-quantity"
                    label="Quantity"
                    type="number"
                    min="1"
                    :max="submissionMaxQuantity"
                    :disabled="!bySubmission.eventId"
                    :value="bySubmission.quantity"
                    @input="bySubmission.quantity = $event !== '' ? Number($event) : 1"
                  />
                </div>

              </template>

              <!-- Actions (shared) -->
              <form-alert :message="alert.message.value" :type="alert.type.value" />
              <p
                v-if="draft && draft.settings && draft.settings.event_members_allowed && selectedMember && selectedMember.is_event_member"
                class="mb-2"
              >
                If your nomination is accepted, you will be charged $100 of event membership fee per draft.
              </p>
              <div class="d-flex flex-wrap align-items-center mt-3 nom-add__actions">
                <app-button
                  type="button"
                  class="mr-3"
                  :block="false"
                  :loading="submitting"
                  :disabled="!canSubmit || submitting"
                  @click="handleSubmit"
                >
                  Add Nominations
                </app-button>
                <router-link
                  v-if="activeTab !== 'submission'"
                  class="btn btn-primary"
                  to="/add-horse"
                >
                  Add Horses
                </router-link>
              </div>

            </div>

            <!-- Desktop sponsor banner -->
            <appearances-drafts-desktop-banner :appearances="appearances" />
          </div>

          <!-- All nominations in draft -->
          <nominations-add-list
            v-if="$isUser || $isVolunteer || $isAdmin || ($isSecretaryMember && (!selectedMember || selectedMember.is_family))"
            :user-nominations="userNominations"
            :loading-nominations="loadingNominations"
            :target-user-id="$isAdmin && selectedMember ? selectedMember.id : null"
            @reload="onListReload"
          />

        </template>

        <!-- Desktop banner shown alone when form is not visible (error/loading state) -->
        <div v-if="loadFailed" class="nom-add__layout">
          <div class="nom-add__forms" />
          <appearances-drafts-desktop-banner :appearances="appearances" />
        </div>

        <!-- Footer: mobile sponsor banner (fixed bottom), hidden once the user scrolls to the page bottom -->
        <div class="footer">
          <transition name="banner-fade">
            <div v-if="appearances.sponsor_logo_mobile && !bannerHidden" class="banner">
              <img :src="appearances.sponsor_logo_mobile">
            </div>
          </transition>
        </div>

      </div>
    </div>
  </tab-layout>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted } from '@vue/composition-api'
import Multiselect from '@/components/AppMultiselect'
import Draggable from 'vuedraggable'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import AppButton from '@/components/AppButton'
import AppTabSwitcher from '@/components/AppTabSwitcher'
import FCheckbox from '@/components/FormFields/FCheckbox'
import FText from '@/components/FormFields/FText'
import FormAlert from '@/components/FormAlert'
import AppTooltip from '@/components/AppTooltip'
import AppearancesDraftsHeader from '@/views/Leaders/Listing/components/AppearancesDraftsHeader'
import AppearancesDraftsDesktopBanner from '@/views/Leaders/Listing/components/AppearancesDraftsDesktopBanner'
import useAppearance from '@/composables/appearances/useAppearance'
import useNominationAdd from '@/composables/nominations/useNominationAdd'
import { formatDate } from '@/utils/date'
import biosecurityApi from '@/services/api/biosecurity'
import NominationsAddList from './components/NominationsAddList'

export default defineComponent({
  name: 'NominationsAdd',
  components: {
    TabLayout,
    PageHeading,
    AppButton,
    AppTabSwitcher,
    FCheckbox,
    FText,
    FormAlert,
    Multiselect,
    Draggable,
    AppTooltip,
    AppearancesDraftsHeader,
    AppearancesDraftsDesktopBanner,
    NominationsAddList
  },
  props: {
    draftId: { type: [String, Number], required: true }
  },
  setup(props) {
    const { form, previews } = useAppearance('nominations')

    const {
      draft,
      loading,
      loadFailed,
      draftEvents,
      memberSearchResults,
      loadingMembers,
      selectedMember,
      byEvent,
      byHorse,
      bySubmission,
      submitting,
      alert,
      userNominations,
      loadingNominations,
      needsToBeProcessed,
      init,
      searchMembers,
      setMember,
      setByEventEvent,
      toggleHorse,
      setByHorseHorse,
      setBySubmissionEvent,
      submit,
      loadUserNominations
    } = useNominationAdd(props.draftId)

    const activeTab = ref('event')

    const hasNoHorseEvents = computed(() => bySubmission.events.length > 0)

    const tabItems = computed(() => {
      const items = [
        { value: 'event', label: 'Nominate by event' },
        { value: 'horse', label: 'Nominate by horse' }
      ]
      if (hasNoHorseEvents.value) {
        items.push({ value: 'submission', label: 'Nominate without a horse' })
      }
      return items
    })

    const submissionMaxQuantity = computed(() => {
      if (bySubmission.maxSubmissions == null) return null
      const remaining = bySubmission.maxSubmissions - (bySubmission.used || 0)
      return remaining > 0 ? remaining : 0
    })

    const appearances = computed(() => ({ ...previews, ...form }))

    const bannerHidden = ref(false)
    function onScroll() {
      if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 10) {
        bannerHidden.value = true
      }
    }
    onMounted(() => window.addEventListener('scroll', onScroll))
    onUnmounted(() => window.removeEventListener('scroll', onScroll))

    const colorRgb = computed(() => {
      const hex = (appearances.value.schema_color || '#aa2d41').replace('#', '')
      const r = parseInt(hex.substring(0, 2), 16)
      const g = parseInt(hex.substring(2, 4), 16)
      const b = parseInt(hex.substring(4, 6), 16)
      return `${r}, ${g}, ${b}`
    })

    const canSubmit = computed(() => {
      if (selectedMember.value == null) return false
      if (activeTab.value === 'event') {
        return byEvent.eventId != null && byEvent.selectedHorseIds.length > 0
      }
      if (activeTab.value === 'submission') {
        if (bySubmission.eventId == null || bySubmission.quantity == null) return false
        if (bySubmission.quantity < 1) return false
        if (submissionMaxQuantity.value != null && bySubmission.quantity > submissionMaxQuantity.value) return false
        return true
      }
      if (byHorse.horseId == null || byHorse.selectedEventId == null) return false
      const selectedEv = byHorse.events.find(e => e.id === byHorse.selectedEventId)
      return selectedEv == null || (!selectedEv.nominated && !selectedEv.nominated_in_combined)
    })

    onMounted(init)

    function onMemberOpen() {
      if (memberSearchResults.value.length === 0) {
        searchMembers('')
      }
    }

    const orderedSelectedHorses = ref([])

    function onHorseToggle(horseId) {
      const found = byEvent.horses.find(h => h.id === horseId)
      if (found != null && found.disabled) return
      toggleHorse(horseId)
      const isNowSelected = byEvent.selectedHorseIds.indexOf(horseId) !== -1
      if (isNowSelected) {
        const horse = byEvent.horses.find(h => h.id === horseId)
        if (horse) orderedSelectedHorses.value.push(horse)
      } else {
        const idx = orderedSelectedHorses.value.findIndex(h => h.id === horseId)
        if (idx !== -1) orderedSelectedHorses.value.splice(idx, 1)
      }
    }

    function onPreviewDragEnd() {
      const newOrder = orderedSelectedHorses.value.map(h => h.id)
      byEvent.selectedHorseIds.splice(0, byEvent.selectedHorseIds.length, ...newOrder)
    }

    function eventNomTooltip(ev) {
      if (ev.nominated) return 'Already nominated'
      if (ev.nominated_in_combined) return 'Already nominated in combined event'
      return ''
    }

    function onMemberInput(member) {
      setMember(member)
      orderedSelectedHorses.value = []
    }

    // Secretary/admin hint: why an otherwise-unfinancial member is nominatable (CAM-1485)
    function memberVolunteerNote(member) {
      if (member == null) return ''
      if (member.is_volunteer) return 'Volunteer'
      if (member.is_volunteer_family) return 'Volunteer family'
      return ''
    }

    function onByEventEventInput(ev) {
      setByEventEvent(ev != null ? ev.id : null)
      orderedSelectedHorses.value = []
    }

    function onByHorseHorseInput(horse) {
      setByHorseHorse(horse != null ? horse.id : null)
    }

    function onBySubmissionEventInput(ev) {
      setBySubmissionEvent(ev != null ? ev.id : null)
    }

    function reloadHorseList() {
      if (activeTab.value === 'event' && byEvent.eventId != null && selectedMember.value != null) {
        setByEventEvent(byEvent.eventId)
      } else if (activeTab.value === 'horse' && byHorse.horseId != null && selectedMember.value != null) {
        setByHorseHorse(byHorse.horseId)
      }
    }

    async function handleSubmit() {
      const ok = await submit(activeTab.value)
      if (ok) {
        orderedSelectedHorses.value = []
        reloadHorseList()
      }
    }

    async function onListReload() {
      const userId = selectedMember.value != null ? selectedMember.value.id : null
      await loadUserNominations(userId, true)
      reloadHorseList()
    }

    const downloadingBlankFormFlag = ref(false)
    async function downloadBlankForm() {
      downloadingBlankFormFlag.value = true
      try {
        const res = await biosecurityApi.getBlankPdf()
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        window.open(blobUrl, '_blank')
      } catch (_) { // handled by http interceptor
      } finally {
        downloadingBlankFormFlag.value = false
      }
    }

    return {
      draft,
      loading,
      loadFailed,
      draftEvents,
      memberSearchResults,
      loadingMembers,
      selectedMember,
      byEvent,
      byHorse,
      bySubmission,
      submissionMaxQuantity,
      submitting,
      alert,
      userNominations,
      loadingNominations,
      needsToBeProcessed,
      activeTab,
      tabItems,
      hasNoHorseEvents,
      appearances,
      bannerHidden,
      colorRgb,
      canSubmit,
      orderedSelectedHorses,
      searchMembers,
      setMember,
      toggleHorse,
      eventNomTooltip,
      onHorseToggle,
      memberVolunteerNote,
      onBySubmissionEventInput,
      onPreviewDragEnd,
      onMemberInput,
      onMemberOpen,
      onByEventEventInput,
      onByHorseHorseInput,
      handleSubmit,
      onListReload,
      formatDate,
      downloadingBlankForm: downloadingBlankFormFlag,
      downloadBlankForm
    }
  }
})
</script>

<style scoped>
.nom-add__layout {
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-gap: 16px;
  padding: 0 16px 16px;
  font-size: 1.1rem;
}

.biosecurity-actions__download {
  margin-left: 1rem;
  justify-content: center;
}

.nom-add__actions {
  row-gap: 12px;
}

@media (max-width: 450px) {
  .biosecurity-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .biosecurity-actions > * {
    margin-top: 12px;
  }

  .biosecurity-actions__download {
    margin-left: 0;
  }
}


.nominations__item {
  margin-top: 12px;
  margin-bottom: 12px;
}

.nom-add__select {
  max-width: 320px;
}

.nom-add__label {
  min-width: 200px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .nom-add__layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .nom-add__field {
    flex-direction: column;
    align-items: stretch !important;
  }

  .nom-add__label {
    min-width: 0;
    margin-bottom: 6px;
  }

  .nom-add__select {
    max-width: none;
  }
}

.horse-order-preview {
  max-width: 293px;
  border: 1px solid var(--success-color);
  background-color: var(--success-bg);
  padding: 6px 5px 0;
}

.horse-order-preview__item {
  text-align: center;
  color: var(--dark);
  font: bold 15px Arial, sans-serif;
  padding: 10px;
  margin-bottom: 5px;
  transition: all 0.3s ease;
  cursor: pointer;
  user-select: none;
}

.horse-order-preview__item:hover {
  background: var(--gray-100);
}

.horse-order-preview__item:active {
  cursor: move;
}

.horse-order-preview__num {
  margin-right: 4px;
}

.banner-fade-enter-active,
.banner-fade-leave-active {
  transition: opacity 1s;
}

.banner-fade-enter,
.banner-fade-leave-to {
  opacity: 0;
}

</style>

<style>
.add-nomination-container .footer {
  display: none;
}

@media (max-width: 568px) {
  .add-nomination-container .footer {
    display: block;
  }

  .add-nomination-container .footer .banner {
    position: fixed;
    bottom: 0;
    width: 100%;
    left: 0;
    padding: 0 16px;
  }

  .add-nomination-container .footer .banner img {
    width: 100%;
  }
}


@media (max-width: 768px) {
  .nom-add__layout .desktop-banner {
    display: none !important;
  }
}

</style>



// WEBPACK FOOTER //
// src/views/Nominations/NominationsAdd.vue